import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { SimpleBarChart } from '@components/Charts';
import KeyValueTooltip from '@components/Charts/KeyValueTooltip';
import { getValue, parseNumberToSignificant } from '@helpers';
import { useDisplayUnit } from '@hooks';
import { DISPLAY_UNIT } from '@constants/index';
import { Paper, Stack, Typography } from '@esgian/esgianui';
import { findEquipmentComponent } from '@api/helpers';
import { NoDataAvailable } from '@components/Display';

function TotRegeneratedEnergyDrawworks({ equipmentData, loading }) {
  const { getUnitTitle, displayUnit, convertUnitValue } = useDisplayUnit();

  const { series = [], unit = '' } = useMemo(() => {
    if (!equipmentData) return {};
    const { componentCalculations } = equipmentData;
    const reusedEnergyComponent = findEquipmentComponent(componentCalculations, 'ReusedEnergy');
    const DC_Break_Res_Energy = reusedEnergyComponent.signals.find(signal => signal.name === 'DC_Break_Res_Energy');
    const VFD_Reused_Energy = reusedEnergyComponent.signals.find(signal => signal.name === 'VFD_Reused_Energy');
    if (!DC_Break_Res_Energy || !VFD_Reused_Energy) return {};

    const vfdValues = VFD_Reused_Energy.timeSeriesValues || {};
    const dcValues = DC_Break_Res_Energy.timeSeriesValues || {};
    if (Object.keys(vfdValues).length === 0 && Object.keys(dcValues).length === 0) return {};

    let reused = Object.values(vfdValues).reduce((a, b) => a + getValue(b), 0);
    let burned = Object.values(dcValues).reduce((a, b) => a + getValue(b), 0);
    if (reused === 0 && burned === 0) return {};
    let max = reused > burned ? reused : burned;
    let displayUnit = DISPLAY_UNIT.ENERGY;
    const { title, converted } = getUnitTitle(max, displayUnit);
    reused = converted ? convertUnitValue(reused, displayUnit) : parseNumberToSignificant(reused);
    burned = converted ? convertUnitValue(burned, displayUnit) : parseNumberToSignificant(burned);
    return { series: [{ name: 'Regenerated Energy', data: [reused, burned] }], unit: title };
  }, [equipmentData]);
  const categories = useMemo(() => ['Reused', 'Burned'], []);

  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={1}>
        <Typography variant={'h6'}>Regenerated Energy from Drawworks</Typography>
        {!loading && NoDataAvailable.hasNoData(series) ? (
          <NoDataAvailable height="295px" />
        ) : (
        <SimpleBarChart
          legendOffsetX={-35}
          legendOffsetY={-10}
          primaryTitleOffsetX={unit.length >= 3 ? 0 : 40}
          height={295}
          primaryXAxisLabel={unit}
          primaryTitleOffsetY={0}
          loading={loading}
          categories={categories}
          series={series}
          customTooltipContent={(series, dataPointIndex) => (
            <KeyValueTooltip
              displayUnit={displayUnit}
              label={categories[dataPointIndex]}
              value={series[0][dataPointIndex]}
              unit={unit}
            />
          )}
          id={'regenerated-energy-drawworks-chart'}
        />
        )}
      </Stack>
    </Paper>
  );
}

TotRegeneratedEnergyDrawworks.propTypes = {
  equipmentData: PropTypes.shape({
    rigId: PropTypes.number,
    rigName: PropTypes.string,
    componentCalculations: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        chartConfig: PropTypes.object,
        signals: PropTypes.arrayOf(          
          PropTypes.shape({
            name: PropTypes.string,
            timeSeriesValues: PropTypes.object,
            metricType: PropTypes.string
          })
        )
      })
    )
  }),
  loading: PropTypes.bool
};

TotRegeneratedEnergyDrawworks.defaultProps = { equipmentData: null, loading: false };

export default TotRegeneratedEnergyDrawworks;
